import { parseEventTime } from "./parseEventTime";

const SITE_URL = "https://playmahjong.in";
const SITE_NAME = "PlayMahjong.in";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function startDateTimeIso(event) {
  const dateStr = event?.date;
  if (!dateStr || typeof dateStr !== "string") return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;

  const slotStart = Array.isArray(event.slots) && event.slots[0]?.start_time;
  const minutes = parseEventTime(event.start_time || event.time || slotStart);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  // Events are scheduled in India. Offset is informational for crawlers;
  // the wall-clock date/time on the event is the source of truth.
  return `${dateStr}T${pad2(hours)}:${pad2(mins)}:00+05:30`;
}

function absoluteImage(image) {
  if (!image || image === "None") return `${SITE_URL}/og-default.jpg`;
  if (/^https?:\/\//i.test(image)) return image;
  if (image.startsWith("/")) return `${SITE_URL}${image}`;
  return `${SITE_URL}/${image}`;
}

/**
 * schema.org Event JSON-LD for a public event detail page.
 * Returns null when the event shouldn't be advertised to crawlers
 * (missing, private, cancelled, or unapproved hosted).
 * Keep in sync with frontend/api/eventJsonLd.mjs (OG HTML + node tests).
 * Real event fields only — no offers, ratings, or invented cities.
 */
export function eventToJsonLd(event, eventId) {
  if (!event || !eventId) return null;
  if (event.is_private) return null;
  if (event.status === "cancelled") return null;
  if (event.event_type === "guest_host" && event.approval_status && event.approval_status !== "approved") {
    return null;
  }

  const startDate = startDateTimeIso(event);
  if (!startDate) return null;

  const name = event.event_name || event.venue_name;
  if (!name) return null;

  const data = {
    "@context": "https://schema.org",
    "@type": "Event",
    name,
    startDate,
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: "https://schema.org/EventScheduled",
    url: `${SITE_URL}/events/${eventId}`,
    image: absoluteImage(event.image_url),
    organizer: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
    },
  };

  if (event.tagline) data.description = event.tagline;
  else if (event.custom_message) data.description = String(event.custom_message).slice(0, 200);

  const placeName = event.venue_name || event.city;
  if (placeName || event.address || event.city) {
    data.location = {
      "@type": "Place",
      name: placeName || SITE_NAME,
    };
    const address = {};
    if (event.address) address.streetAddress = event.address;
    // City is the GEO locality (which city). Neighborhood `area` is not a substitute.
    if (event.city) address.addressLocality = event.city;
    else if (event.area) address.addressLocality = event.area;
    if (event.city) address.addressRegion = event.city;
    address.addressCountry = "IN";
    if (Object.keys(address).length) data.location.address = address;
  }

  return data;
}

export function websiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: "en-IN",
    description: "Find and join local Mahjong games in your area. Sign up for events and get matched with players at your skill level.",
  };
}
